import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadMatchVideo, getAnalysisStatus } from '../utils/api';
import { Upload, Video, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import './UploadPage.css';

const STAGES = [
  { key: 'upload',   label: 'Uploading video to server',       pct: 15 },
  { key: 'extract',  label: 'Extracting match data',           pct: 30 },
  { key: 'detect',   label: 'Detecting formations & players',  pct: 50 },
  { key: 'analyze',  label: 'Running Gemini AI analysis',      pct: 70 },
  { key: 'generate', label: 'Generating tactical report',      pct: 90 },
  { key: 'done',     label: 'Analysis complete!',              pct: 100 },
];

export default function UploadPage() {
  const navigate  = useNavigate();
  const inputRef  = useRef(null);
  const pollRef   = useRef(null);

  const [file, setFile]         = useState(null);
  const [dragging, setDragging] = useState(false);
  const [meta, setMeta]         = useState({
    homeTeam: '', awayTeam: '', matchDate: '', competition: '',
    homeScore: '', awayScore: ''
  });
  const [state, setState]       = useState('idle');
  const [progress, setProgress] = useState(0);
  const [stage, setStage]       = useState(0);
  const [error, setError]       = useState('');

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const pickFile = useCallback((f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['mp4','mov','avi','mkv'].includes(ext)) {
      setError('Please select a valid video file (.mp4, .mov, .avi, .mkv)');
      return;
    }
    setError('');
    setFile(f);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer?.files?.[0] || e.target?.files?.[0]);
  }, [pickFile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;

    setError('');
    setState('uploading');
    setProgress(5);
    setStage(0);

    try {
      const res = await uploadMatchVideo(file, meta, (pct) => {
        setProgress(5 + pct * 0.1);
      });

      const id = res.data.matchId;
      setProgress(15);
      setStage(1);
      setState('processing');

      let currentStage = 1;
      const stageTimer = setInterval(() => {
        currentStage = Math.min(currentStage + 1, STAGES.length - 2);
        setStage(currentStage);
        setProgress(STAGES[currentStage].pct);
      }, 4000);

      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await getAnalysisStatus(id);
          const { status } = statusRes.data;

          if (status === 'complete') {
            clearInterval(pollRef.current);
            clearInterval(stageTimer);
            setStage(STAGES.length - 1);
            setProgress(100);
            setState('done');
            setTimeout(() => navigate(`/reports/${id}`), 1200);
          } else if (status === 'error') {
            clearInterval(pollRef.current);
            clearInterval(stageTimer);
            setState('error');
            setError(statusRes.data.error || 'Analysis failed');
          } else if (attempts >= 90) {
            // Stop polling and hand off to the report page rather than
            // claiming "done" here — it re-fetches the real status and will
            // honestly show "still processing" if that's genuinely the case,
            // instead of us faking a completed state client-side.
            clearInterval(pollRef.current);
            clearInterval(stageTimer);
            navigate(`/reports/${id}`);
          }
        } catch (err) {
          // A 404 here means the server has no record of this analysis at
          // all anymore (e.g. it restarted and lost in-memory/local state,
          // since this deployment has no persistent DB) — that match will
          // never complete, so silently retrying forever just leaves the
          // user staring at a frozen progress bar. Surface it honestly
          // instead. Other errors (network blips, rate limits) are still
          // worth a silent retry on the next tick.
          if (err.response?.status === 404) {
            clearInterval(pollRef.current);
            clearInterval(stageTimer);
            setState('error');
            setError('This analysis was lost — the server restarted while it was processing (this deployment currently has no persistent storage). Please try uploading again.');
          }
        }
      }, 2000);

    } catch (err) {
      setState('error');
      setError(err.response?.data?.error || err.message || 'Upload failed');
    }
  };

  const fmt = (b) => b > 1e9 ? `${(b/1e9).toFixed(1)} GB` : `${(b/1e6).toFixed(1)} MB`;

  const resetForm = () => {
    setState('idle');
    setFile(null);
    setProgress(0);
    setStage(0);
    setError('');
  };

  return (
    <div className="upload-page fade-in">
      <div className="upload-page__header">
        <div className="upload-page__eyebrow"><Upload size={12} /> New Analysis</div>
        <h1 className="upload-page__title">Upload a match video</h1>
        <p className="upload-page__sub">Drop full-match footage — Gemini AI extracts formations, pressing, players, and coaching recommendations.</p>
      </div>

      {(state === 'idle' || state === 'error') && (
        <form onSubmit={handleSubmit} className="upload-form">

          {/* Dropzone */}
          <div
            className={`dropzone card ${dragging ? 'dropzone--active' : ''} ${file ? 'dropzone--filled' : ''}`}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onClick={() => !file && inputRef.current?.click()}
          >
            <input ref={inputRef} type="file" accept=".mp4,.mov,.avi,.mkv,video/*" onChange={onDrop} hidden />
            {file ? (
              <div className="dropzone__file">
                <Video size={28} style={{ color: 'var(--accent-primary)' }} />
                <div className="dropzone__file-info">
                  <div className="dropzone__file-name">{file.name}</div>
                  <div className="dropzone__file-size">{fmt(file.size)}</div>
                </div>
                <button type="button" className="dropzone__remove" onClick={(e) => { e.stopPropagation(); setFile(null); }}>
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="dropzone__empty">
                <div className="dropzone__icon"><Upload size={28} /></div>
                <div className="dropzone__title">Drop match video here</div>
                <div className="dropzone__sub">MP4 · MOV · AVI · MKV — up to 500MB</div>
              </div>
            )}
          </div>

          {error && <div className="upload-error"><AlertCircle size={15} /> {error}</div>}

          {/* Match Details */}
          <div className="card upload-meta">
            <h3 className="upload-meta__title">Match Details</h3>
            <div className="upload-meta__grid">
              <div className="form-group">
                <label>Home Team</label>
                <input type="text" placeholder="e.g. Manchester City" value={meta.homeTeam}
                  onChange={e => setMeta(m => ({ ...m, homeTeam: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Away Team</label>
                <input type="text" placeholder="e.g. Arsenal" value={meta.awayTeam}
                  onChange={e => setMeta(m => ({ ...m, awayTeam: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Competition</label>
                <input type="text" placeholder="e.g. Premier League" value={meta.competition}
                  onChange={e => setMeta(m => ({ ...m, competition: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Match Date</label>
                <input type="date" value={meta.matchDate}
                  onChange={e => setMeta(m => ({ ...m, matchDate: e.target.value }))} />
              </div>
            </div>

            {/* Score row */}
            <div className="score-input-row">
              <div className="score-input-label">
                Final Score <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional but recommended)</span>
              </div>
              <div className="score-input-fields">
                <div className="score-team">
                  <span>{meta.homeTeam || 'Home'}</span>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    placeholder="0"
                    value={meta.homeScore}
                    onChange={e => setMeta(m => ({ ...m, homeScore: e.target.value }))}
                    className="score-number-input"
                  />
                </div>
                <div className="score-divider-text">—</div>
                <div className="score-team score-team--right">
                  <input
                    type="number"
                    min="0"
                    max="20"
                    placeholder="0"
                    value={meta.awayScore}
                    onChange={e => setMeta(m => ({ ...m, awayScore: e.target.value }))}
                    className="score-number-input"
                  />
                  <span>{meta.awayTeam || 'Away'}</span>
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary upload-submit" disabled={!file}>
            <Upload size={16} /> Analyze Match Video
          </button>
        </form>
      )}

      {(state === 'uploading' || state === 'processing' || state === 'done') && (
        <div className="upload-progress card fade-in">
          <div className="upload-progress__header">
            {state === 'done'
              ? <CheckCircle size={48} style={{ color: 'var(--accent-green)' }} />
              : <Loader size={36} className="spin" style={{ color: 'var(--accent-primary)' }} />
            }
            <h2>{state === 'done' ? 'Analysis Complete!' : 'Analysing Match...'}</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
              {state === 'done' ? 'Redirecting to your report...' : 'Gemini AI is generating your tactical report. Usually 15–30 seconds.'}
            </p>
          </div>

          <div className="upload-progress__bar">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%`, transition: 'width 0.8s ease' }} />
            </div>
            <div className="upload-progress__pct">{Math.round(progress)}%</div>
          </div>

          <div className="upload-stages">
            {STAGES.map((s, i) => (
              <div key={s.key} className={`upload-stage ${i < stage ? 'done' : i === stage ? 'active' : ''}`}>
                <div className="upload-stage__dot">
                  {i < stage
                    ? <CheckCircle size={14} style={{ color: 'var(--accent-green)' }} />
                    : i === stage
                      ? <Loader size={12} className="spin" style={{ color: 'var(--accent-primary)' }} />
                      : <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--border)' }} />
                  }
                </div>
                <span>{s.label}</span>
              </div>
            ))}
          </div>

          {state !== 'done' && (
            <button type="button" className="btn btn-ghost" style={{ marginTop: 16, fontSize: 12 }} onClick={resetForm}>
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}
